import axios from 'axios';
import { TSearchSuggestionResults } from '../components/containers/InputSearch/types';
import { getParam, pushParam, buildQuery, buildUrl } from './urlHelper';
import {
  searchResultsQueryParams,
  defaultSearchFilter,
  defaultSearchResultsPaging,
  categoryFilters,
} from '../components/containers/SearchResults/constants';
import {
  TAddSearchClient,
  WindowObject,
  TSearchHit,
  TdefaultSearchResultsPaging,
  TSearchResultsQueryParams,
  TStatsEvent,
} from '../components/containers/SearchResults/types';
import { WHITE_SPACE_REGEX, HTTP_REGEX } from './regex';

import { PropEndpoints } from '../shared/device/apiConfig';
import { SEARCH_CLEAR_PARAM, SEARCH_FOCUS_PARAM } from '../shared/search/constants';
import { isLocal } from './util';
import { TCoreService } from '../shared/types/apiTypes';
import { CORE_SERVICE_KEY } from '../components/containers/BroadbandPlanCard/constants';

export const getUrlFilterParam = (): string => getParam(window.location.search, searchResultsQueryParams.f);
export const getUrlSearchParam = (): string => getParam(window.location.search, searchResultsQueryParams.searchResults);
export const getUrlPageSizeParam = (): string => getParam(window.location.search, searchResultsQueryParams.ps);

export const setUrlFilterParam = (filter: string): void => pushParam(searchResultsQueryParams.f, filter);
export const setUrlSearchParam = (search: string): void => pushParam(searchResultsQueryParams.searchResults, search);
export const setUrlPageSizeParam = (pageSize: string): void => pushParam(searchResultsQueryParams.ps, pageSize);

export const shouldFocusSearchInput = (): boolean => getParam(window.location.search, SEARCH_FOCUS_PARAM) === 'true';
export const shouldClearSearchInput = (): boolean => getParam(window.location.search, SEARCH_CLEAR_PARAM) === 'true';

export const resetPageSizeParam = (): void => setUrlPageSizeParam(defaultSearchResultsPaging.pageSize.toString());

export const searchLandingUrl = (redirectUrl: string, search: string, filter: string, pageSize: string): string => {
  const params: TSearchResultsQueryParams = { f: filter, ps: pageSize, searchResults: search };
  return `${redirectUrl}${buildQuery(params)}`;
};

export const redirectSearchResults = (
  propEndpoints: any,
  search: string,
  filter: string,
  propSearchRedirect: string
): void => {
  if (propSearchRedirect === '0') {
    setUrlSearchParam(search);
    return;
  }
  const { searchPageResults } = JSON.parse(propEndpoints) as PropEndpoints;
  window.location.replace(
    searchLandingUrl(searchPageResults, search, filter, `${defaultSearchResultsPaging.pageSize}`)
  );
};

export const addSearchClient = (siteKey: string): TAddSearchClient => {
  const { AddSearchClient } = (window as unknown) as WindowObject;

  if (!siteKey) {
    return null;
  }

  return new AddSearchClient(siteKey);
};

export const createArrayOfSearchResultStrings = (results: TSearchSuggestionResults): string[] => {
  const { suggestions } = results;
  const res = suggestions.reduce((acc, current) => {
    return [...acc, current.value];
  }, []);
  return res;
};

export const styleSuggestionResult = (result: string, userSearch: string): string => {
  if (!result || (userSearch || '').replace(WHITE_SPACE_REGEX, '') === '') {
    return '';
  }
  const match = userSearch.toLowerCase().includes(result.toLowerCase().replace(WHITE_SPACE_REGEX, ''));
  const searchResultExactMatch = match ? ` search-result-exact-match` : ``;
  return `search-results-text${searchResultExactMatch}`;
};

export const isPinnedResult = (result: TSearchHit) => {
  return result?.type === 'PINNED';
};

export const validSearchFilter = (filter: string) => {
  return categoryFilters.findIndex(x => x.filter === filter) > -1;
};

export const getSearchResults = async (
  userSearch: string,
  propSearchSiteKey: string,
  callback: Function,
  filter: string,
  pageSettings: TdefaultSearchResultsPaging,
  enableLocal?: boolean,
  dataFile?: string
): Promise<void> => {
  const client = addSearchClient(propSearchSiteKey);
  if (!client) {
    return;
  }

  if (isLocal() && enableLocal) {
    const url = buildUrl('', '', enableLocal, dataFile);
    const data = await axios.get(url);
    callback(data.data);
    return;
  }

  if (filter !== defaultSearchFilter && validSearchFilter(filter)) {
    client.setCategoryFilters(filter);
  }

  if (pageSettings) {
    client.setPaging(`${pageSettings.page}`, `${pageSettings.pageSize}`, pageSettings.sortBy, pageSettings.sortOrder);
  }

  client.search(userSearch, callback);
};

// For the time being, it is nice to see if we can see results coming through from url.
export const getSuggestionResults = async (
  userSearch: string,
  propSearchSiteKey: string,
  callBack: Function,
  enableLocal?: boolean,
  dataFile?: string
) => {
  const client = addSearchClient(propSearchSiteKey);
  if (!client) {
    return;
  }

  if (isLocal() && enableLocal) {
    const url = buildUrl('', '', enableLocal, dataFile);
    const data = await axios.get(url);
    callBack(data.data);
    return;
  }

  client.suggestions(userSearch, callBack);
};

export const sendStatsEvent = async (siteKey: string, keyword: string, data: TStatsEvent) => {
  const client = addSearchClient(siteKey);
  if (!client) {
    return;
  }

  if (!data.documentId || !data.position || !keyword) {
    return;
  }

  await client.sendStatsEvent('click', keyword, data);
};

export const urlDisplayName = (url: string): string => {
  // removes https://, http and www... in front of the url string
  return (url || '').replace(HTTP_REGEX, '');
};

export const userEnteredOnlyWhiteSpace = (userSearch: string): boolean =>
  (userSearch || '').replace(WHITE_SPACE_REGEX, '').length === 0;

export const shouldRedirectToSearchResults = (userSearch: string): boolean => !userEnteredOnlyWhiteSpace(userSearch);

export const getAvailablePlans = (coreServices: Array<TCoreService>): string[] => {
  return [
    ...coreServices
      .filter(cp => cp.coreServiceKey?.toLocaleLowerCase() === CORE_SERVICE_KEY.naked)
      .flatMap(service =>
        service?.coreProducts.map(product => product.coreProductBroadbandCategory?.toLocaleLowerCase())
      ),
    ...coreServices
      .filter(cp => cp.coreServiceKey?.toLocaleLowerCase() === CORE_SERVICE_KEY.wireless)
      .flatMap(service => service?.coreProducts.map(product => product.coreProductKey?.toLocaleLowerCase())),
  ];
};
